import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { BreadcrumbSchema } from "@/components/StructuredData";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { 
  CheckCircle, 
  Award, 
  Users, 
  Clock, 
  Shield,
  ArrowRight,
  Hammer,
  Heart,
  Target
} from "lucide-react";

const BREADCRUMBS = [
  { name: "Home", url: "/" },
  { name: "About Us", url: "/about" }
];

const WHY_CHOOSE_ITEMS = [
  { id: "why-free-estimates", text: "Free, no-obligation estimates" },
  { id: "why-fixed-price", text: "Fixed-price quotes - no hidden costs" },
  { id: "why-management", text: "End-to-end project management" },
  { id: "why-direct", text: "Direct communication with Tony" },
  { id: "why-rates", text: "Competitive local rates" },
  { id: "why-clean", text: "Clean, tidy, and professional" },
  { id: "why-references", text: "References available on request" },
  { id: "why-insurance", text: "Full public liability insurance" },
  { id: "why-flexible", text: "Flexible scheduling" }
];

const VALUES = [
  { id: "value-quality", icon: Hammer, title: "Quality Craftsmanship", description: "Every project receives the same attention to detail, from small repairs to major renovations." },
  { id: "value-timely", icon: Clock, title: "Timely Completion", description: "We respect your time and ensure projects are completed within agreed timelines." },
  { id: "value-customer", icon: Heart, title: "Customer Focused", description: "Your satisfaction is our priority. We maintain clear communication throughout every project." },
  { id: "value-pricing", icon: Target, title: "Transparent Pricing", description: "No hidden costs. We provide detailed, fixed-price quotes before any work begins." }
];

const TIMELINE = [
  { id: "timeline-2014", year: "2014", event: "Tony qualified as a professional carpenter" },
  { id: "timeline-2016", year: "2016", event: "Expanded into kitchen and bathroom fitting" },
  { id: "timeline-2018", year: "2018", event: "Established TJP Carpentry & Construction" },
  { id: "timeline-2020", year: "2020", event: "Completed 100th major project" },
  { id: "timeline-2024", year: "2024", event: "Celebrating 10+ years in the industry" }
];

const AboutPage = () => {

  return (
    <div className="min-h-screen bg-stone-50">
      <SEO 
        title="About TJP Construction | Experienced Surrey Builder"
        description="Meet Tony Payne, experienced builder serving Surrey & SW London for over 10 years. Quality craftsmanship on every project. Fully insured."
        keywords="about tjp carpentry, tony payne carpenter, surrey builder, experienced carpenter"
        url="/about"
      />
      <BreadcrumbSchema items={BREADCRUMBS} />
      <Header />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-stone-100 to-stone-200 py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-6">
              <p className="text-sm font-medium tracking-wide uppercase text-amber-700">
                About Us
              </p>
              <h1 className="text-4xl md:text-5xl font-black tracking-tight text-slate-900">
                Building Trust Through Quality Work
              </h1>
              <p className="text-lg text-stone-600 leading-relaxed">
                TJP Carpentry &amp; Construction is a local Surrey and SW London builder 
                offering professional carpentry and renovation services. Founded by 
                Tony Payne, we&apos;ve built our reputation on quality craftsmanship, 
                timely completion, and exceptional customer service.
              </p>
              <div className="flex flex-wrap gap-4 pt-4">
                <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow">
                  <Shield className="text-green-600" size={18} />
                  <span className="text-sm font-medium">Fully Insured</span>
                </div>
                <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow">
                  <Award className="text-amber-600" size={18} />
                  <span className="text-sm font-medium">10+ Years Experience</span>
                </div>
                <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow">
                  <Users className="text-blue-600" size={18} />
                  <span className="text-sm font-medium">200+ Happy Clients</span>
                </div>
              </div>
            </div>

            <div className="relative">
              <img
                src="/images/projects/Extension4.JPG"
                alt="Tony Payne at work"
                className="rounded-lg shadow-2xl object-cover w-full h-[500px]"
                loading="lazy"
                decoding="async"
                width={600}
                height={500}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Meet Tony Section */}
      <section className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <img
                src="/images/projects/Wardrobe2.JPG"
                alt="Carpentry workshop"
                className="rounded-lg shadow-xl object-cover w-full h-[400px]"
                loading="lazy"
                decoding="async"
                width={600}
                height={400}
              />
            </div>

            <div className="order-1 lg:order-2 space-y-6">
              <p className="text-sm font-medium tracking-wide uppercase text-amber-700">
                Meet The Founder
              </p>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900">
                Tony Payne
              </h2>
              <p className="text-lg text-stone-600 leading-relaxed">
                Tony is a fully qualified carpenter with over a decade of experience 
                in the construction industry. His journey began with traditional 
                carpentry training, and over the years, he has expanded his expertise 
                to include kitchen and bathroom fitting, giving him a comprehensive 
                understanding of home renovation projects.
              </p>
              <p className="text-stone-600 leading-relaxed">
                &quot;I believe in doing the job right the first time. Every client 
                deserves the same level of care and attention, regardless of the 
                project size. That&apos;s why I personally oversee every job from start 
                to finish.&quot;
              </p>
              <blockquote className="border-l-4 border-amber-700 pl-6 italic text-stone-600">
                &quot;Quality craftsmanship isn&apos;t just about the finished product – 
                it&apos;s about the entire experience, from the first conversation 
                to the final handover.&quot;
              </blockquote>
            </div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-20 md:py-32 bg-stone-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <p className="text-sm font-medium tracking-wide uppercase text-amber-700 mb-4">
              What We Stand For
            </p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900">
              Our Core Values
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {VALUES.map((value) => (
              <div
                key={value.id}
                className="bg-white rounded-lg p-8 shadow-lg border border-stone-100 text-center"
              >
                <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <value.icon className="text-amber-700" size={32} />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{value.title}</h3>
                <p className="text-stone-600 text-sm leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20 md:py-32 bg-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <p className="text-sm font-medium tracking-wide uppercase text-amber-700 mb-4">
              Our Journey
            </p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900">
              Building Excellence Since 2014
            </h2>
          </div>

          <div className="relative">
            <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-stone-200"></div>
            <div className="space-y-8">
              {TIMELINE.map((item) => (
                <div key={item.id} className="relative pl-20">
                  <div className="absolute left-4 w-8 h-8 bg-amber-700 rounded-full flex items-center justify-center">
                    <CheckCircle className="text-white" size={16} />
                  </div>
                  <div className="bg-stone-50 rounded-lg p-6">
                    <p className="text-amber-700 font-bold text-lg">{item.year}</p>
                    <p className="text-stone-600">{item.event}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 md:py-32 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <p className="text-sm font-medium tracking-wide uppercase text-amber-500 mb-4">
              The TJP Difference
            </p>
            <h2 className="text-3xl md:text-4xl font-bold">
              Why Choose TJP Carpentry?
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {WHY_CHOOSE_ITEMS.map((item) => (
              <div key={item.id} className="flex items-center gap-3">
                <CheckCircle className="text-amber-500 flex-shrink-0" size={20} />
                <span className="text-stone-300">{item.text}</span>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/">
              <Button className="bg-amber-700 text-white hover:bg-amber-600 h-14 px-10 font-bold">
                Get Your Free Estimate
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;
